import React, { useState } from 'react';
import { Button, Stack, Text, Textarea, useToast, VStack } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { addCatalogueItemByText } from '../../hooks/catalogueItems';
import {
  CatalogueHero,
  CatalogueSectionCard,
  CatalogueShell,
  PrimaryCatalogueButton,
} from './components/catalogueUi';

export default function PasteTextAI() {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleProcess = async () => {
    if (!orgId || !text.trim()) {
      toast({
        title: 'Add product context first',
        description: 'Paste enough product details for the AI flow to infer a useful item.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    const response = await addCatalogueItemByText(orgId, text);
    setLoading(false);

    if (!response.success) {
      toast({
        title: 'AI item creation failed',
        description: 'The product could not be generated from this text.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Product generated',
      description: 'The AI-assisted product has been added to your catalogue.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/catalogue`);
  };

  return (
    <CatalogueShell compact>
      <CatalogueHero
        eyebrow="AI entry"
        title="Turn raw product notes into a catalogue item"
        description="Paste supplier notes, a product brief, or unstructured descriptions. This flow is designed to reduce typing without feeling detached from the main catalogue system."
      />

      <Stack spacing={6}>
        <CatalogueSectionCard
          title="Source text"
          description="Use clear product context. Include the name, key details, and any pricing clues you already know."
        >
          <VStack align="stretch" spacing={4}>
            <Textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              minH={{ base: '240px', md: '320px' }}
              borderRadius="18px"
              bg="gray.50"
              placeholder="Paste product details here..."
            />
            <Text fontSize="sm" color="gray.500">
              Strong input produces cleaner generated items and reduces editing later.
            </Text>
          </VStack>
        </CatalogueSectionCard>

        <VStack align="stretch" spacing={3}>
          <PrimaryCatalogueButton
            onClick={handleProcess}
            isLoading={loading}
            loadingText="Generating"
          >
            Generate item from text
          </PrimaryCatalogueButton>
          <Button borderRadius="16px" variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </VStack>
      </Stack>
    </CatalogueShell>
  );
}
